import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

import { supabase } from '../../lib/supabase';

const CustomerQuotePortal = () => {
  const [searchEmail, setSearchEmail] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [actionError, setActionError] = useState('');
  const [customerInfo, setCustomerInfo] = useState(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchInput?.trim()) return;
    setLoading(true);
    setError('');
    setQuotes([]);
    setCustomerInfo(null);
    setSelectedQuote(null);

    try {
      const email = searchInput?.trim()?.toLowerCase();

      const { data: customer, error: custErr } = await supabase
        ?.from('customers')
        ?.select('id, name, email, phone, company')
        ?.ilike('email', email)
        ?.single();

      if (custErr || !customer) {
        setError('No customer account found with that email address. Please contact your service representative.');
        setLoading(false);
        return;
      }

      setCustomerInfo(customer);
      setSearchEmail(email);

      const { data: quotesData, error: quotesErr } = await supabase
        ?.from('quotes')
        ?.select(`
          id, quote_number, total_amount, notes, created_at, expected_service_date,
          approval_date, pdf_url,
          status:quote_statuses(name, color),
          salesperson:personnel!quotes_salesperson_id_fkey(name, email, phone),
          quote_products(id, name, quantity, price)
        `)
        ?.eq('customer_id', customer?.id)
        ?.order('created_at', { ascending: false });

      if (quotesErr) throw quotesErr;
      setQuotes(quotesData || []);
    } catch (err) {
      setError('Failed to load quotes. Please try again.');
      console.error('Portal search error:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveQuote = async () => {
    if (!selectedQuote) return;
    setApproving(true);
    setActionError('');
    setActionSuccess('');

    try {
      const { data: approvedStatus, error: statusErr } = await supabase
        ?.from('quote_statuses')
        ?.select('id')
        ?.ilike('name', 'approved')
        ?.single();

      if (statusErr || !approvedStatus) throw new Error('Could not find approved status');

      const { error: updateErr } = await supabase
        ?.from('quotes')
        ?.update({
          status_id: approvedStatus?.id,
          approval_date: new Date()?.toISOString(),
          notes: selectedQuote?.notes
            ? `${selectedQuote?.notes}\n\n[Customer Approved Online] ${customerNotes ? 'Customer notes: ' + customerNotes : ''}`
            : `[Customer Approved Online] ${customerNotes ? 'Customer notes: ' + customerNotes : ''}`
        })
        ?.eq('id', selectedQuote?.id);

      if (updateErr) throw updateErr;

      setActionSuccess('Quote approved successfully! Your service representative will be in touch to schedule the work.');
      setSelectedQuote(prev => ({ ...prev, status: { name: 'Approved', color: '#22c55e' } }));
      setQuotes(prev => prev?.map(q =>
        q?.id === selectedQuote?.id
          ? { ...q, status: { name: 'Approved', color: '#22c55e' } }
          : q
      ));
      setCustomerNotes('');
    } catch (err) {
      setActionError('Failed to approve quote. Please contact your service representative.');
      console.error('Approve error:', err?.message);
    } finally {
      setApproving(false);
    }
  };

  const handleRejectQuote = async () => {
    if (!selectedQuote || !customerNotes?.trim()) {
      setActionError('Please provide a reason for declining the quote.');
      return;
    }
    setRejecting(true);
    setActionError('');
    setActionSuccess('');

    try {
      const { data: rejectedStatus, error: statusErr } = await supabase
        ?.from('quote_statuses')
        ?.select('id')
        ?.ilike('name', 'rejected')
        ?.single();

      if (statusErr || !rejectedStatus) throw new Error('Could not find rejected status');

      const { error: updateErr } = await supabase
        ?.from('quotes')
        ?.update({
          status_id: rejectedStatus?.id,
          notes: selectedQuote?.notes
            ? `${selectedQuote?.notes}\n\n[Customer Declined Online] Reason: ${customerNotes}`
            : `[Customer Declined Online] Reason: ${customerNotes}`
        })
        ?.eq('id', selectedQuote?.id);

      if (updateErr) throw updateErr;

      setActionSuccess('Quote declined. Your service representative has been notified.');
      setSelectedQuote(prev => ({ ...prev, status: { name: 'Rejected', color: '#ef4444' } }));
      setQuotes(prev => prev?.map(q =>
        q?.id === selectedQuote?.id
          ? { ...q, status: { name: 'Rejected', color: '#ef4444' } }
          : q
      ));
      setCustomerNotes('');
    } catch (err) {
      setActionError('Failed to decline quote. Please contact your service representative.');
      console.error('Reject error:', err?.message);
    } finally {
      setRejecting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusStyle = (statusName) => {
    const s = statusName?.toLowerCase();
    if (s === 'approved') return 'bg-green-100 text-green-700 border-green-200';
    if (s === 'rejected') return 'bg-red-100 text-red-700 border-red-200';
    if (s === 'sent') return 'bg-blue-100 text-blue-700 border-blue-200';
    if (s === 'draft') return 'bg-gray-100 text-gray-600 border-gray-200';
    if (s === 'converted') return 'bg-purple-100 text-purple-700 border-purple-200';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const canActOnQuote = (quote) => {
    const s = quote?.status?.name?.toLowerCase();
    return s === 'sent' || s === 'draft';
  };

  const lineItemTotal = (quote) => {
    return quote?.quote_products?.reduce((sum, p) => sum + (Number(p?.price || 0) * Number(p?.quantity || 1)), 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Helmet>
        <title>Customer Quote Portal - RIGJOCKEY</title>
      </Helmet>

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Wrench" size={22} color="var(--color-primary)" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-heading font-semibold text-gray-900">RIGJOCKEY</h1>
            <p className="text-xs sm:text-sm text-gray-500">Customer Quote Portal</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Search Section */}
        {!customerInfo ? (
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Icon name="FileSearch" size={32} color="var(--color-primary)" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-gray-900 mb-2">View Your Quotes</h2>
              <p className="text-gray-500 text-sm sm:text-base">Enter your email address to access your quotes and approve or decline service proposals.</p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
              <form onSubmit={handleSearch} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Mail" size={18} color="#9ca3af" />
                    </div>
                    <input
                      type="email"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e?.target?.value)}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <Icon name="AlertCircle" size={16} color="#ef4444" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-6 bg-primary text-primary-foreground rounded-xl font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Looking up quotes...</>
                  ) : (
                    <><Icon name="Search" size={16} color="currentColor" /> Find My Quotes</>
                  )}
                </button>
              </form>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6">
              Having trouble? Contact your service representative for assistance.
            </p>
          </div>
        ) : (
          /* Customer Found - Show Quotes */
          <div>
            {/* Customer Info Bar */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{customerInfo?.name}</p>
                  <p className="text-sm text-gray-500">{customerInfo?.email}</p>
                </div>
              </div>
              <button
                onClick={() => { setCustomerInfo(null); setQuotes([]); setSearchInput(''); setSelectedQuote(null); setError(''); }}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Icon name="LogOut" size={16} color="currentColor" />
                Sign Out
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Quotes List */}
              <div className="lg:col-span-2">
                <h2 className="text-lg font-heading font-semibold text-gray-900 mb-4">
                  Your Quotes ({quotes?.length})
                </h2>

                {quotes?.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                    <Icon name="FileText" size={32} color="#9ca3af" />
                    <p className="mt-3 text-sm text-gray-500">No quotes found for your account.</p>
                    <p className="text-xs text-gray-400 mt-1">Contact your service representative to request a quote.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {quotes?.map(quote => (
                      <button
                        key={quote?.id}
                        onClick={() => { setSelectedQuote(quote); setActionSuccess(''); setActionError(''); setCustomerNotes(''); }}
                        className={`w-full text-left bg-white rounded-xl border transition-all p-4 hover:shadow-md ${
                          selectedQuote?.id === quote?.id
                            ? 'border-primary shadow-md ring-2 ring-primary/20'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-sm font-semibold text-gray-900">{quote?.quote_number}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusStyle(quote?.status?.name)}`}>
                            {quote?.status?.name || 'Unknown'}
                          </span>
                        </div>
                        <p className="text-lg font-bold text-gray-900 mb-1">{formatCurrency(quote?.total_amount)}</p>
                        <p className="text-xs text-gray-400">{formatDate(quote?.created_at)}</p>
                        {canActOnQuote(quote) && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-primary font-medium">
                            <Icon name="MousePointerClick" size={12} color="currentColor" />
                            Action required
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Quote Detail */}
              <div className="lg:col-span-3">
                {!selectedQuote ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-10 text-center h-full flex flex-col items-center justify-center">
                    <Icon name="FileText" size={40} color="#d1d5db" />
                    <p className="mt-3 text-gray-400 text-sm">Select a quote to view details</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    {/* Quote Header */}
                    <div className="p-5 sm:p-6 border-b border-gray-100">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                        <div>
                          <h3 className="text-xl font-heading font-semibold text-gray-900">{selectedQuote?.quote_number}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">Created {formatDate(selectedQuote?.created_at)}</p>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusStyle(selectedQuote?.status?.name)}`}>
                          {selectedQuote?.status?.name || 'Unknown'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {selectedQuote?.expected_service_date && (
                          <div className="flex items-center gap-2">
                            <Icon name="Calendar" size={16} color="#6b7280" />
                            <div>
                              <p className="text-xs text-gray-400">Expected Service Date</p>
                              <p className="text-sm font-medium text-gray-700">{formatDate(selectedQuote?.expected_service_date)}</p>
                            </div>
                          </div>
                        )}
                        {selectedQuote?.salesperson && (
                          <div className="flex items-center gap-2">
                            <Icon name="User" size={16} color="#6b7280" />
                            <div>
                              <p className="text-xs text-gray-400">Your Representative</p>
                              <p className="text-sm font-medium text-gray-700">{selectedQuote?.salesperson?.name}</p>
                              {selectedQuote?.salesperson?.email && (
                                <p className="text-xs text-gray-400">{selectedQuote?.salesperson?.email}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Line Items */}
                    {selectedQuote?.quote_products?.length > 0 && (
                      <div className="p-5 sm:p-6 border-b border-gray-100">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Services & Items</h4>
                        <div className="space-y-2">
                          {selectedQuote?.quote_products?.map(item => (
                            <div key={item?.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                              <div>
                                <p className="text-sm font-medium text-gray-800">{item?.name}</p>
                                <p className="text-xs text-gray-400">Qty: {item?.quantity}</p>
                              </div>
                              <p className="text-sm font-semibold text-gray-900">
                                {formatCurrency(Number(item?.price || 0) * Number(item?.quantity || 1))}
                              </p>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-200">
                          <span className="text-sm font-semibold text-gray-700">Total</span>
                          <span className="text-xl font-bold text-gray-900">{formatCurrency(selectedQuote?.total_amount)}</span>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {selectedQuote?.notes && (
                      <div className="p-5 sm:p-6 border-b border-gray-100">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Notes from Your Representative</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedQuote?.notes}</p>
                      </div>
                    )}

                    {/* Action Section */}
                    {canActOnQuote(selectedQuote) && (
                      <div className="p-5 sm:p-6 bg-blue-50 border-t border-blue-100">
                        <h4 className="text-sm font-semibold text-gray-800 mb-1">Your Response</h4>
                        <p className="text-xs text-gray-500 mb-4">Please review the quote above and approve or decline below. You can add optional notes.</p>

                        <div className="mb-4">
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">Notes (optional for approval, required for decline)</label>
                          <textarea
                            value={customerNotes}
                            onChange={(e) => setCustomerNotes(e?.target?.value)}
                            placeholder="Add any comments or questions..."
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                          />
                        </div>

                        {actionError && (
                          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
                            <Icon name="AlertCircle" size={14} color="#ef4444" />
                            <p className="text-xs text-red-700">{actionError}</p>
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={handleApproveQuote}
                            disabled={approving || rejecting}
                            className="flex-1 py-3 px-4 bg-green-600 text-white rounded-xl font-medium text-sm hover:bg-green-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                          >
                            {approving ? (
                              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Approving...</>
                            ) : (
                              <><Icon name="ThumbsUp" size={16} color="currentColor" /> Approve Quote</>
                            )}
                          </button>
                          <button
                            onClick={handleRejectQuote}
                            disabled={approving || rejecting}
                            className="flex-1 py-3 px-4 bg-white border border-red-300 text-red-600 rounded-xl font-medium text-sm hover:bg-red-50 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                          >
                            {rejecting ? (
                              <><div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" /> Declining...</>
                            ) : (
                              <><Icon name="ThumbsDown" size={16} color="currentColor" /> Decline Quote</>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Success Message */}
                    {actionSuccess && (
                      <div className="p-5 sm:p-6 bg-green-50 border-t border-green-100">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon name="CheckCircle" size={18} color="#16a34a" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-green-800 mb-1">Response Recorded</p>
                            <p className="text-sm text-green-700">{actionSuccess}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Already actioned */}
                    {!canActOnQuote(selectedQuote) && !actionSuccess && (
                      <div className="p-5 sm:p-6 border-t border-gray-100">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Icon name="Info" size={16} color="#9ca3af" />
                          {selectedQuote?.status?.name?.toLowerCase() === 'approved' ?'This quote has been approved. Your service representative will contact you to schedule the work.'
                            : selectedQuote?.status?.name?.toLowerCase() === 'rejected' ?'This quote has been declined. Contact your representative if you have questions.'
                            : selectedQuote?.status?.name?.toLowerCase() === 'converted' ?'This quote has been converted to a service ticket. Work is being scheduled.' :'This quote is not currently available for action.'}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="mt-12 py-6 border-t border-gray-200 bg-white">
        <p className="text-center text-xs text-gray-400">
          &copy; {new Date()?.getFullYear()} RIGJOCKEY Field Service Management. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default CustomerQuotePortal;
